import { useQuery } from "@tanstack/react-query";
import { API_URL, fetchWithTimeout, getPathScope, throwIfNotOk } from "./core";
import { createRestMutation } from "./hook-factory";

// ============================================================
// Types
// ============================================================

/**
 * Inference modalities a provider can serve. A provider omitting a modality
 * from its `capabilities` falls back to its built-in static selection.
 */
export type InferenceModality = "text" | "image" | "stt" | "tts";

/** Human labels for the non-text modalities (text is assumed for every provider). */
const EXTRA_MODALITY_LABELS: Record<
	Exclude<InferenceModality, "text">,
	string
> = {
	image: "Image",
	stt: "Speech-to-text",
	tts: "Text-to-speech",
};

/**
 * The provider's modalities BEYOND text, as display labels. Every provider does
 * text (chat), so we only surface the extras — an empty array means text-only.
 */
export function extraModalityLabels(
	modalities: readonly InferenceModality[],
): string[] {
	return modalities
		.filter((m): m is Exclude<InferenceModality, "text"> => m !== "text")
		.map((m) => EXTRA_MODALITY_LABELS[m]);
}

/**
 * Per-modality upstream override. All fields optional — an empty block means
 * "serve this modality with the provider's static defaults". `base_url` and
 * `models_endpoint` only apply to OpenAI-SDK-compatible kinds.
 */
export interface CapabilityBlock {
	base_url?: string;
	model?: string;
	models_endpoint?: string;
}

export type InferenceCapabilities = Partial<
	Record<InferenceModality, CapabilityBlock>
>;

/**
 * A provider as returned by the list endpoint. The API key is NEVER included
 * in any read — only `POST /:slug/key` writes it.
 */
export interface InferenceProviderItem {
	id: number;
	slug: string;
	kind: string;
	displayName: string;
	capabilities: InferenceCapabilities;
	/** True when any modality carries a custom base_url/model. */
	hasCustomUpstream: boolean;
	/** True for the single provider flagged as this org's default model source. */
	isDefault: boolean;
	status: string;
	createdAt: string | null;
}

interface InferenceProvidersPayload {
	providers: InferenceProviderItem[];
}

/** Auth mechanism a catalog provider supports. */
export type ProviderAuthType = "oauth" | "device-code" | "api-key";

/**
 * A provider in the catalog (GET /inference-providers/catalog). Sourced from the
 * module registry, so it includes OAuth providers (Claude/ChatGPT) alongside the
 * API-key ones. Carries public pre-fill metadata only — the key stays
 * user-supplied. The slug IS the catalog id (and the default `kind` on create).
 */
export interface InferenceCatalogItem {
	slug: string;
	displayName: string;
	iconUrl: string;
	authType: ProviderAuthType;
	supportedAuthTypes: ProviderAuthType[];
	/** Wire protocol the provider speaks; auth support is declared separately. */
	sdkCompat: string | null;
	baseUrl: string;
	defaultModel: string | null;
	modelsEndpoint: string | null;
	/** True when the deployment provides this credential for every org. */
	systemAvailable: boolean;
	/**
	 * Static fallback model IDs — used by the model picker when a provider has no
	 * live `modelsEndpoint` (e.g. OAuth providers) or the live fetch is empty.
	 */
	models: string[];
	apiKeyPlaceholder: string;
	apiKeyInstructions: string;
	/** Modalities this provider serves; drives which overrides the UI offers. */
	modalities: InferenceModality[];
}

/** Does this catalog provider offer OAuth sign-in (vs API key only)? */
export function supportsOAuth(item: InferenceCatalogItem): boolean {
	return item.supportedAuthTypes.some(
		(t) => t === "oauth" || t === "device-code",
	);
}

/**
 * API-key setup requires both a runnable protocol and API-key authentication.
 * Subscription-only providers sign in instead. The server enforces the same gate.
 */
export function isApiKeyAddable(item: InferenceCatalogItem): boolean {
	return item.sdkCompat != null && item.supportedAuthTypes.includes("api-key");
}

interface InferenceCatalogPayload {
	catalog: InferenceCatalogItem[];
}

// ============================================================
// URL helpers
// ============================================================

function inferenceProvidersUrl(orgSlug: string, path = "") {
	// Routes live inside the agents router, mounted at /api/:orgSlug/agents
	// (server index.ts: app.route("/api/:orgSlug/agents", agentRoutes)), so the
	// /agents/ segment stays even though these providers are org-scoped.
	return `${API_URL}/api/${orgSlug}/agents/inference-providers${path}`;
}

// ============================================================
// Queries
// ============================================================

/** GET /api/:org/inference-providers — org-owned providers (never the key). */
export function useInferenceProviders(options?: { enabled?: boolean }) {
	const { slug } = getPathScope();
	return useQuery({
		queryKey: ["inference-providers", slug],
		queryFn: async (): Promise<InferenceProviderItem[]> => {
			const res = await fetchWithTimeout(inferenceProvidersUrl(slug ?? ""), {
				credentials: "include",
			});
			await throwIfNotOk(res, "Failed to load inference providers");
			const data = (await res.json()) as InferenceProvidersPayload;
			return data.providers ?? [];
		},
		enabled: !!slug && (options?.enabled ?? true),
		staleTime: 30_000,
	});
}

/** GET /api/:org/inference-providers/catalog — bundled providers (public). */
export function useInferenceProviderCatalog(options?: { enabled?: boolean }) {
	const { slug } = getPathScope();
	return useQuery({
		queryKey: ["inference-providers-catalog", slug],
		queryFn: async (): Promise<InferenceCatalogItem[]> => {
			const res = await fetchWithTimeout(
				inferenceProvidersUrl(slug ?? "", "/catalog"),
				{ credentials: "include" },
			);
			await throwIfNotOk(res, "Failed to load inference provider catalog");
			const data = (await res.json()) as InferenceCatalogPayload;
			return data.catalog ?? [];
		},
		enabled: !!slug && (options?.enabled ?? true),
		staleTime: 60_000,
	});
}

// ============================================================
// Mutations
// ============================================================

/** POST /api/:org/inference-providers — create a provider (with its one key). */
export const useCreateInferenceProvider = createRestMutation<
	[],
	{
		slug: string;
		kind: string;
		displayName?: string;
		apiKey: string;
		capabilities?: InferenceCapabilities;
	},
	{ provider: InferenceProviderItem }
>({
	method: "POST",
	url: ({ slug }) => inferenceProvidersUrl(slug),
	body: (params) => params,
	invalidate: () => [["inference-providers"]],
	successMessage: "Provider created",
	errorMessage: "Failed to create provider",
});

/**
 * PUT /api/:org/inference-providers/:slug/capabilities/:modality — replace one
 * modality's upstream block. Passing an empty block clears the override.
 */
export const useUpdateInferenceCapability = createRestMutation<
	[],
	{
		providerSlug: string;
		modality: InferenceModality;
		block: CapabilityBlock;
	},
	{ provider: InferenceProviderItem }
>({
	method: "PUT",
	url: ({ slug, params }) =>
		inferenceProvidersUrl(
			slug,
			`/${params.providerSlug}/capabilities/${params.modality}`,
		),
	body: (params) => ({ block: params.block }),
	invalidate: () => [["inference-providers"]],
	successMessage: "Capability updated",
	errorMessage: "Failed to update capability",
});

/**
 * PUT /api/:org/inference-providers/:slug — edit a provider's core fields. Only
 * displayName is mutable (slug and kind are immutable).
 */
export const useUpdateInferenceProvider = createRestMutation<
	[],
	{ providerSlug: string; displayName: string },
	{ provider: InferenceProviderItem }
>({
	method: "PUT",
	url: ({ slug, params }) =>
		inferenceProvidersUrl(slug, `/${params.providerSlug}`),
	body: (params) => ({ displayName: params.displayName }),
	invalidate: () => [["inference-providers"]],
	successMessage: "Provider updated",
	errorMessage: "Failed to update provider",
});

/**
 * PUT /api/:org/inference-providers/:slug/default — flag this provider as the
 * org's default model source (the last fallback layer). Clears the flag on any
 * previous default server-side; the list query re-fetches to reflect it.
 */
export const useSetDefaultInferenceProvider = createRestMutation<
	[],
	{ providerSlug: string },
	{ success: boolean; slug: string; isDefault: true }
>({
	method: "PUT",
	url: ({ slug, params }) =>
		inferenceProvidersUrl(slug, `/${params.providerSlug}/default`),
	invalidate: () => [["inference-providers"]],
	successMessage: "Default provider set",
	errorMessage: "Failed to set default provider",
});

/** PUT /api/:org/inference-providers/:slug/key — rotate the provider's key. */
export const useRotateInferenceKey = createRestMutation<
	[],
	{ providerSlug: string; value: string },
	{ success: boolean }
>({
	method: "PUT",
	url: ({ slug, params }) =>
		inferenceProvidersUrl(slug, `/${params.providerSlug}/key`),
	body: (params) => ({ value: params.value }),
	invalidate: () => [["inference-providers"]],
	successMessage: "Key rotated",
	errorMessage: "Failed to rotate key",
});

/** DELETE /api/:org/inference-providers/:slug — remove a provider. */
export const useDeleteInferenceProvider = createRestMutation<
	[],
	{ providerSlug: string },
	{ success: boolean }
>({
	method: "DELETE",
	url: ({ slug, params }) =>
		inferenceProvidersUrl(slug, `/${params.providerSlug}`),
	invalidate: () => [["inference-providers"]],
	successMessage: "Provider deleted",
	errorMessage: "Failed to delete provider",
});

/**
 * Whether a provider's `kind` (= its catalog slug) is one an API key can be
 * pointed at, given the catalog. Drives whether the base_url /
 * models_endpoint override UI is offered — a subscription kind speaks a
 * routable protocol but has no endpoint an operator may re-aim. Replaces the
 * old hardcoded `kind === "openai"` check (which wrongly hid the override UI
 * for every non-"openai" slug like Cerebras). Unknown kinds (provider dropped
 * from the catalog) default to true so their existing overrides stay editable.
 */
export function kindIsApiKeyAddable(
	kind: string,
	catalog: readonly InferenceCatalogItem[],
): boolean {
	const entry = catalog.find((c) => c.slug === kind);
	return entry ? isApiKeyAddable(entry) : true;
}

// ============================================================
// Client-side validation — mirrors the server's validateCapabilityBlock so the
// UI fails fast. The server re-validates; this is UX-only.
// ============================================================

/**
 * Validate a `base_url`: https:// only, no `user:pass@`, no query/hash.
 * Returns an error string, or null when valid (empty is valid = omitted).
 */
function validateBaseUrl(value: string): string | null {
	const trimmed = value.trim();
	if (!trimmed) return null;
	let url: URL;
	try {
		url = new URL(trimmed);
	} catch {
		return "Enter a valid URL";
	}
	if (url.protocol !== "https:") return "Must start with https://";
	if (url.username || url.password)
		return "Credentials in the URL aren't allowed";
	if (url.search || url.hash) return "Query or fragment isn't allowed";
	return null;
}

const MODELS_ENDPOINT_RE = /^\/[A-Za-z0-9/_.-]*$/;

/**
 * Validate a `models_endpoint`: must match `^/[A-Za-z0-9/_.-]*$` and not start
 * with `//`. Returns an error string, or null when valid (empty = omitted).
 */
function validateModelsEndpoint(value: string): string | null {
	const trimmed = value.trim();
	if (!trimmed) return null;
	if (trimmed.startsWith("//")) return "Can't start with //";
	if (!MODELS_ENDPOINT_RE.test(trimmed)) {
		return "Must be a path like /v1/models";
	}
	return null;
}

/**
 * Validate a `model`: non-empty when provided. Returns an error string, or null
 * when valid (empty = omitted).
 */
function validateModel(value: string): string | null {
	// Empty is allowed (omitted); a whitespace-only string is not a valid model.
	if (value.length > 0 && !value.trim()) return "Model can't be blank";
	return null;
}

export interface CapabilityFieldErrors {
	base_url?: string;
	model?: string;
	models_endpoint?: string;
}

/** Validate a whole capability block; returns per-field errors (empty = ok). */
export function validateCapabilityBlock(
	block: CapabilityBlock,
): CapabilityFieldErrors {
	const errors: CapabilityFieldErrors = {};
	const baseUrlErr = validateBaseUrl(block.base_url ?? "");
	if (baseUrlErr) errors.base_url = baseUrlErr;
	const modelErr = validateModel(block.model ?? "");
	if (modelErr) errors.model = modelErr;
	const endpointErr = validateModelsEndpoint(block.models_endpoint ?? "");
	if (endpointErr) errors.models_endpoint = endpointErr;
	return errors;
}

export function hasCapabilityErrors(errors: CapabilityFieldErrors): boolean {
	return Boolean(errors.base_url || errors.model || errors.models_endpoint);
}
